import { StorageHelper } from "./storageHelper";

const CHAT_HISTORY_KEY = "ask_flashdice_chat_histories";

/**
 * Get all chat histories from localStorage
 * @returns {Array} Array of chat history objects
 */
export const getChatHistories = () => {
  const histories = StorageHelper.get(CHAT_HISTORY_KEY);
  return Array.isArray(histories) ? histories : [];
};

/**
 * Save chat histories to localStorage
 * @param {Array} histories - Array of chat history objects
 */
export const saveChatHistories = (histories) => {
  StorageHelper.set(CHAT_HISTORY_KEY, histories);
  // Dispatch custom event to notify components of chat history updates
  window.dispatchEvent(new CustomEvent("chatHistoryUpdated"));
};

/**
 * Get a specific chat history by ID
 * @param {string} chatId - The chat history ID
 * @returns {Object|null} Chat history object or null if not found
 */
export const getChatHistory = (chatId) => {
  const histories = getChatHistories();
  return histories.find((chat) => chat.id === chatId) || null;
};

/**
 * Create a new chat history entry
 * @param {string} title - Title for the chat (usually first user message)
 * @param {Array} messages - Array of messages
 * @param {string} sessionId - Session ID for this chat
 * @returns {Object} New chat history object
 */
export const createChatHistory = (title, messages = [], sessionId) => {
  return {
    id: `chat_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
    title: title || "New Chat",
    timestamp: new Date().toISOString(),
    messages: messages,
    sessionId: sessionId,
  };
};

/**
 * Add or update a chat history
 * @param {Object} chatHistory - Chat history object to save
 */
export const saveChatHistory = (chatHistory) => {
  const histories = getChatHistories();
  const existingIndex = histories.findIndex((chat) => chat.id === chatHistory.id);

  if (existingIndex >= 0) {
    // Update existing chat
    histories[existingIndex] = {
      ...histories[existingIndex],
      ...chatHistory,
      timestamp: chatHistory.timestamp || histories[existingIndex].timestamp,
    };
  } else {
    // Add new chat at the beginning
    histories.unshift(chatHistory);
  }

  // Limit to last 50 chats to avoid localStorage size issues
  const limitedHistories = histories.slice(0, 50);
  saveChatHistories(limitedHistories);
};

/**
 * Delete a chat history
 * @param {string} chatId - The chat history ID to delete
 */
export const deleteChatHistory = (chatId) => {
  const histories = getChatHistories();
  const filtered = histories.filter((chat) => chat.id !== chatId);
  saveChatHistories(filtered);
};

/**
 * Update chat history title (usually from first user message)
 * @param {string} chatId - The chat history ID
 * @param {string} title - New title
 */
export const updateChatHistoryTitle = (chatId, title) => {
  const histories = getChatHistories();
  const chat = histories.find((c) => c.id === chatId);
  if (chat) {
    chat.title = title;
    saveChatHistories(histories);
  }
};

/**
 * Update chat history messages
 * @param {string} chatId - The chat history ID
 * @param {Array} messages - Updated messages array
 */
export const updateChatHistoryMessages = (chatId, messages) => {
  const histories = getChatHistories();
  const chat = histories.find((c) => c.id === chatId);
  if (chat) {
    chat.messages = messages;
    // Update timestamp to reflect last activity
    chat.timestamp = new Date().toISOString();
    saveChatHistories(histories);
  }
};

/**
 * Format timestamp for display
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} Formatted date string
 */
export const formatChatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks}w ago`;
  }

  // For older dates, show formatted date
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

/**
 * Group chat histories by date
 * @param {Array} histories - Array of chat histories
 * @returns {Object} Grouped histories by date category
 */
export const groupChatHistoriesByDate = (histories) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const groups = {
    today: [],
    yesterday: [],
    week: [],
    older: [],
  };

  histories.forEach((chat) => {
    const chatDate = new Date(chat.timestamp);
    if (chatDate >= today) {
      groups.today.push(chat);
    } else if (chatDate >= yesterday) {
      groups.yesterday.push(chat);
    } else if (chatDate >= weekAgo) {
      groups.week.push(chat);
    } else {
      groups.older.push(chat);
    }
  });

  return groups;
};

